/**
 * Dataset analysis page.
 *
 * Full flow:
 *   1. Read the Excel selection (values + metadata)
 *   2. Send to backend analysis endpoint (deterministic stats + AI enrichment)
 *   3. Display structured results: summary, column stats, KPIs, charts, anomalies
 *
 * All Office.js calls go through the office/ barrel.
 * The analysis logic lives in the backend service.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Button,
  Text,
  Badge,
  MessageBar,
  MessageBarBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import {
  DocumentSearchRegular,
  DataBarVerticalRegular,
  ArrowResetRegular,
  CopyRegular,
  WarningRegular,
  LightbulbRegular,
  HighlightRegular,
} from '@fluentui/react-icons';
import type { AnalysisResult, AutoInsightResult } from '@excel-ai-studio/shared';
import { analyzeDataset, generateAutoInsights } from '../services/api.client';
import { readSelectionInfo, highlightRange, type SelectionInfo } from '../office';
import { getSettings } from '../store/settings.store';
import { addHistoryEntry } from '../store/history.store';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { ResultCard, type ResultCardAction } from '../components/common/ResultCard';
import { ActionPreviewPanel } from '../components/common/ActionPreviewPanel';
import { ConfidenceBadge } from '../components/common/ConfidenceBadge';
import { AutoInsightPanel } from '../components/features/AutoInsightPanel';

export const AnalysisPage: React.FC = () => {
  // Selection
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>();
  const [selectionLoading, setSelectionLoading] = useState(false);

  // Analysis
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult>();
  const [autoInsightResult, setAutoInsightResult] = useState<AutoInsightResult>();
  const [autoInsightLoading, setAutoInsightLoading] = useState(false);

  // Shared
  const [error, setError] = useState<string>();

  // ─── Helpers ─────────────────────────────────────────

  const resetAll = () => {
    setSelectionInfo(undefined);
    setResult(undefined);
    setAutoInsightResult(undefined);
    setError(undefined);
  };

  const isSelectionEmpty = (values: unknown[][]): boolean =>
    values.every((row) =>
      row.every((cell) => cell === null || cell === undefined || String(cell).trim() === ''),
    );

  // ─── Step 1: Read selection ──────────────────────────

  const handleReadSelection = async () => {
    setError(undefined);
    setResult(undefined);
    setAutoInsightResult(undefined);
    setSelectionLoading(true);

    try {
      const info = await readSelectionInfo();

      if (!info.values || info.values.length === 0) {
        setError('Aucune cellule selectionnee. Selectionnez des donnees dans Excel puis reessayez.');
        setSelectionInfo(undefined);
        return;
      }

      if (isSelectionEmpty(info.values)) {
        setError('La selection ne contient que des cellules vides.');
        setSelectionInfo(undefined);
        return;
      }

      setSelectionInfo(info);
    } catch (err) {
      setError((err as Error).message);
      setSelectionInfo(undefined);
    } finally {
      setSelectionLoading(false);
    }
  };

  // ─── Step 2: Analyze ──────────────────────────────────

  const handleAnalyze = async () => {
    if (!selectionInfo) return;
    const startedAt = Date.now();
    const settings = getSettings();
    setError(undefined);
    setResult(undefined);
    setAutoInsightResult(undefined);
    setAnalysisLoading(true);
    setAutoInsightLoading(true);

    try {
      // Run both analyses in parallel
      const [analysisResponse, autoInsightResponse] = await Promise.all([
        analyzeDataset({
          values: selectionInfo.values,
          language: 'fr',
        }),
        generateAutoInsights({
          values: selectionInfo.values,
          language: 'fr',
        }),
      ]);

      setResult(analysisResponse);
      setAutoInsightResult(autoInsightResponse.result);
      addHistoryEntry(
        'analysis',
        settings.provider,
        settings.model,
        `Analyse dataset ${selectionInfo.rowCount}x${selectionInfo.colCount}`,
        true,
        undefined,
        {
          module: 'analysis',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      addHistoryEntry(
        'analysis',
        settings.provider,
        settings.model,
        `Analyse dataset ${selectionInfo.rowCount}x${selectionInfo.colCount}`,
        false,
        (err as Error).message,
        {
          module: 'analysis',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } finally {
      setAnalysisLoading(false);
      setAutoInsightLoading(false);
    }
  };

  // ─── Render ──────────────────────────────────────────

  return (
    <div>
      {/* ══ Step 1: Selection ══ */}
      <div className="card">
        <Text className="section-title">1. Lire la selection</Text>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          Selectionnez le dataset a analyser dans Excel (incluez les en-tetes si possible).
        </Text>
        <Button
          size="small"
          appearance="primary"
          icon={<DocumentSearchRegular />}
          onClick={handleReadSelection}
          disabled={selectionLoading}
        >
          Lire la selection
        </Button>

        {selectionLoading && <LoadingSpinner label="Lecture de la selection..." />}

        {selectionInfo && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Badge appearance="outline" size="small" color="informative">
              {selectionInfo.address}
            </Badge>
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              {selectionInfo.rowCount} ligne{selectionInfo.rowCount > 1 ? 's' : ''} x{' '}
              {selectionInfo.colCount} colonne{selectionInfo.colCount > 1 ? 's' : ''}
            </Text>
            <Button size="small" appearance="subtle" icon={<ArrowResetRegular />} onClick={resetAll}>
              Reinitialiser
            </Button>
          </div>
        )}
      </div>

      {/* ══ Step 2: Launch analysis ══ */}
      {selectionInfo && !result && (
        <div className="card">
          <Text className="section-title">2. Analyser</Text>
          <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
            L&apos;analyse combine des statistiques deterministes (types, valeurs uniques, vides)
            avec des insights IA (KPIs, graphiques, anomalies, recommandations).
          </Text>
          <Button
            size="small"
            appearance="primary"
            icon={<DataBarVerticalRegular />}
            onClick={handleAnalyze}
            disabled={analysisLoading}
          >
            Lancer l&apos;analyse
          </Button>
        </div>
      )}

      {/* ══ Loading / Error ══ */}
      {(analysisLoading || autoInsightLoading) && (
        <LoadingSpinner label="Analyse en cours (cela peut prendre quelques secondes)..." />
      )}
      {error && <ErrorDisplay message={error} onDismiss={() => setError(undefined)} />}

      {/* ══ Results ══ */}
      {result && <AnalysisResults result={result} autoInsightResult={autoInsightResult} />}
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────

const AnalysisResults: React.FC<{ result: AnalysisResult; autoInsightResult?: AutoInsightResult }> = ({ result, autoInsightResult }) => {
  const [highlightPreview, setHighlightPreview] = useState<string | null>(null);
  const confidenceMeta = useMemo(() => {
    const reasons: string[] = [];
    let score = 70;
    if (result.rowCount >= 10) {
      score += 8;
      reasons.push('Volume de donnees suffisant pour une analyse robuste.');
    } else {
      reasons.push('Peu de lignes: les tendances sont plus fragiles.');
    }
    if (result.headers.length > 0 && result.columnStats.length > 0) {
      score += 8;
      reasons.push('Structure de colonnes detectee correctement.');
    }
    if (result.warnings.length > 0) {
      score -= Math.min(20, result.warnings.length * 5);
      reasons.push('Des avertissements reduisent la fiabilite de certaines conclusions.');
    }
    if (result.columnStats.some((c) => c.type === 'mixed' || c.type === 'empty')) {
      score -= 8;
      reasons.push('Des colonnes mixtes/vides limitent la precision des suggestions.');
    }
    return { score, reasons };
  }, [result]);

  const summaryActions = useMemo<ResultCardAction[]>(() => {
    const summaryText = [
      `${result.rowCount} lignes x ${result.columnCount} colonnes`,
      `En-tetes: ${result.headers.join(', ')}`,
      '',
      result.summary,
    ].join('\n');
    return [
      {
        key: 'copy-summary',
        label: 'Copier le resume',
        icon: <CopyRegular />,
        feedbackLabel: 'Copie !',
        onClick: () => navigator.clipboard.writeText(summaryText),
      },
    ];
  }, [result]);

  // Collect all anomaly locations for batch highlighting
  const anomalyLocations = useMemo(
    () => result.anomalies.filter((a) => a.location).map((a) => a.location!),
    [result.anomalies],
  );

  const handleHighlightConfirm = useCallback(async () => {
    for (const loc of anomalyLocations) {
      await highlightRange(loc, '#FFF3CD');
    }
    setTimeout(() => setHighlightPreview(null), 1500);
  }, [anomalyLocations]);

  const anomalyActions = useMemo<ResultCardAction[]>(() => {
    const list: ResultCardAction[] = [];
    if (anomalyLocations.length > 0) {
      list.push({
        key: 'highlight',
        label: 'Surligner dans Excel',
        icon: <HighlightRegular />,
        primary: true,
        tooltip: 'Surligner les cellules contenant des anomalies',
        disabled: highlightPreview !== null,
        onClick: () => setHighlightPreview(anomalyLocations.join(', ')),
      });
    }
    list.push({
      key: 'copy-anomalies',
      label: 'Copier',
      icon: <CopyRegular />,
      feedbackLabel: 'Copie !',
      onClick: () => {
        const text = result.anomalies.map((a) =>
          `[${a.type}] ${a.description}${a.location ? ` (${a.location})` : ''}`,
        ).join('\n');
        return navigator.clipboard.writeText(text);
      },
    });
    return list;
  }, [anomalyLocations, result.anomalies, highlightPreview]);

  return (
    <div>
      {/* ── Summary card ── */}
      <ResultCard
        title="Resume du dataset"
        icon={<DataBarVerticalRegular />}
        variant="analysis"
        actions={summaryActions}
        metadata={
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Badge appearance="outline" color="informative">{result.rowCount} lignes</Badge>
            <Badge appearance="outline" color="informative">{result.columnCount} colonnes</Badge>
          </div>
        }
      >
        <Text size={200} style={{ whiteSpace: 'pre-wrap' }}>{result.summary}</Text>
        <ConfidenceBadge meta={confidenceMeta} />
      </ResultCard>

      {/* ── Warnings ── */}
      {result.warnings.length > 0 && (
        <ResultCard
          title="Avertissements"
          icon={<WarningRegular />}
          variant="warning"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {result.warnings.map((w, i) => (
              <MessageBar key={i} intent="warning" style={{ borderRadius: 4 }}>
                <MessageBarBody>{w}</MessageBarBody>
              </MessageBar>
            ))}
          </div>
        </ResultCard>
      )}

      {/* ── Detailed accordion sections ── */}
      <Accordion multiple collapsible defaultOpenItems={['columns', 'kpis']}>
        {/* Column Stats */}
        {result.columnStats.length > 0 && (
          <AccordionItem value="columns">
            <AccordionHeader>
              Statistiques par colonne ({result.columnStats.length})
            </AccordionHeader>
            <AccordionPanel>
              <div style={{ overflowX: 'auto' }}>
                <Table size="extra-small" aria-label="Column statistics">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Colonne</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Uniques</TableHeaderCell>
                      <TableHeaderCell>Vides</TableHeaderCell>
                      <TableHeaderCell>Exemples</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.columnStats.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell><Text size={200} weight="semibold">{col.name}</Text></TableCell>
                        <TableCell><Badge size="small" appearance="tint" color={typeColor(col.type)}>{col.type}</Badge></TableCell>
                        <TableCell><Text size={200}>{col.uniqueCount}</Text></TableCell>
                        <TableCell><Text size={200}>{col.emptyCount}</Text></TableCell>
                        <TableCell>
                          <Text size={200} style={{ color: 'var(--text-secondary)' }}>
                            {col.sampleValues.slice(0, 3).join(', ')}
                          </Text>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* KPIs */}
        {result.suggestedKPIs.length > 0 && (
          <AccordionItem value="kpis">
            <AccordionHeader>
              KPIs suggeres ({result.suggestedKPIs.length})
            </AccordionHeader>
            <AccordionPanel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {result.suggestedKPIs.map((kpi, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: 'var(--colorNeutralBackground2)', borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Text size={200} weight="semibold">{kpi.label}</Text>
                      <Text size={300} weight="bold" style={{ color: 'var(--colorBrandForeground1)' }}>{kpi.value}</Text>
                    </div>
                    <Text size={200} style={{ color: 'var(--text-secondary)' }}>{kpi.description}</Text>
                  </div>
                ))}
              </div>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* Charts */}
        {result.suggestedCharts.length > 0 && (
          <AccordionItem value="charts">
            <AccordionHeader>
              Graphiques suggeres ({result.suggestedCharts.length})
            </AccordionHeader>
            <AccordionPanel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {result.suggestedCharts.map((chart, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: 'var(--colorNeutralBackground2)', borderRadius: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Badge size="small" appearance="tint" color="brand">{chart.type}</Badge>
                      <Text size={200} weight="semibold">{chart.title}</Text>
                    </div>
                    <Text size={200} style={{ color: 'var(--text-secondary)' }}>{chart.description}</Text>
                    <Text size={200} style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                      Colonnes : {chart.columns.join(', ')}
                    </Text>
                  </div>
                ))}
              </div>
            </AccordionPanel>
          </AccordionItem>
        )}
      </Accordion>

      {/* ── Anomalies card ── */}
      {result.anomalies.length > 0 && (
        <ResultCard
          title="Anomalies detectees"
          icon={<WarningRegular />}
          variant="warning"
          actions={anomalyActions}
          metadata={
            <Badge appearance="filled" color="severe" size="small">
              {result.anomalies.length}
            </Badge>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {result.anomalies.map((a, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Badge size="small" appearance="tint" color={anomalyColor(a.type)}>{a.type}</Badge>
                <div>
                  <Text size={200}>{a.description}</Text>
                  {a.location && (
                    <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block' }}>
                      Emplacement : {a.location}
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Highlight confirmation panel */}
          {highlightPreview && (
            <ActionPreviewPanel
              impact={{
                target: highlightPreview,
                description: `${anomalyLocations.length} zone${anomalyLocations.length > 1 ? 's' : ''} sera${anomalyLocations.length > 1 ? 'ont' : ''} surlignee${anomalyLocations.length > 1 ? 's' : ''} en jaune dans Excel.`,
                cellsAffected: anomalyLocations.length,
              }}
              onConfirm={handleHighlightConfirm}
              onCancel={() => setHighlightPreview(null)}
              confirmLabel="Surligner"
              confirmIcon={<HighlightRegular />}
            />
          )}
        </ResultCard>
      )}

      {/* ── Recommendations card ── */}
      {result.recommendations.length > 0 && (
        <ResultCard
          title="Recommandations"
          icon={<LightbulbRegular />}
          variant="suggestion"
          metadata={
            <Badge appearance="outline" color="informative" size="small">
              {result.recommendations.length}
            </Badge>
          }
        >
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {result.recommendations.map((r, i) => (
              <li key={i}><Text size={200}>{r}</Text></li>
            ))}
          </ul>
        </ResultCard>
      )}

      {/* ── Auto Insight Panel ── */}
      {autoInsightResult && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--colorNeutralStroke2)' }}>
          <Text className="section-title" style={{ marginBottom: 12 }}>Auto Insight Generator</Text>
          <AutoInsightPanel result={autoInsightResult} />
        </div>
      )}
    </div>
  );
};

// ─── Color helpers ────────────────────────────────────

type BadgeColor = 'brand' | 'danger' | 'important' | 'informative' | 'severe' | 'subtle' | 'success' | 'warning';

function typeColor(type: string): BadgeColor {
  switch (type) {
    case 'number': return 'brand';
    case 'text': return 'informative';
    case 'date': return 'success';
    case 'boolean': return 'warning';
    case 'mixed': return 'severe';
    case 'empty': return 'subtle';
    default: return 'informative';
  }
}

function anomalyColor(type: string): BadgeColor {
  switch (type) {
    case 'outlier': return 'danger';
    case 'missing': return 'warning';
    case 'duplicate': return 'severe';
    case 'inconsistent': return 'important';
    default: return 'informative';
  }
}
